import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { INITIATIVES, FOUNDATION_INFO } from "@/lib/initiatives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, ShieldCheck, Handshake, TrendingUp, Users, Loader2 } from "lucide-react";

const BUDGETS = ["Below ₹1 Lakh", "₹1L – ₹5L", "₹5L – ₹25L", "₹25L – ₹1 Crore", "Above ₹1 Crore"];

export default function CSR() {
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    designation: "",
    budget_range: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleInit = (title) =>
    setSelected((s) => (s.includes(title) ? s.filter((x) => x !== title) : [...s, title]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_person || !form.email || !form.phone) {
      toast.error("Company, contact person, email and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/csr/enquiries", {
        ...form,
        interested_initiatives: selected,
      });
      setDone(true);
      toast.success("Thank you — our team will reach out within 2 working days.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit enquiry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="section-py">
        <div className="mx-auto max-w-7xl container-x grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.24em] text-[#4A5568]">
              For Corporate Partners
            </div>
            <h1 className="mt-3 font-serif-display text-4xl md:text-6xl text-[#0A2540] leading-[1.05]">
              Deploy your CSR where it visibly changes lives.
            </h1>
            <p className="mt-6 text-lg text-[#4A5568] max-w-xl">
              UB Maitreya Foundation is a registered, 80G-approved Section 8 company — a
              compliant CSR partner for companies looking to make measurable impact
              across mental health, education, women's empowerment, MSMEs, farming and
              civic sense.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, title: "80G Approved", body: "Compliant with Section 12AB & 80G of the IT Act, 1961." },
                { icon: Handshake, title: "Co-branded Programs", body: "Custom-designed programs with your identity and reporting." },
                { icon: Users, title: "Employee Engagement", body: "Volunteering, drives and workshops for your teams." },
                { icon: TrendingUp, title: "Measurable Impact", body: "Quarterly outcome reports with third-party auditability." },
              ].map((f) => (
                <div key={f.title} className="rounded-2xl border border-[#0A2540]/10 bg-white p-5">
                  <f.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.75} />
                  <div className="mt-3 font-medium text-[#0A2540]">{f.title}</div>
                  <div className="mt-1 text-sm text-[#4A5568]">{f.body}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl bg-[#0A2540] text-white p-6">
              <div className="text-xs uppercase tracking-[0.24em] text-[#D4AF37]">Compliance credentials</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>CIN: <span className="font-medium">{FOUNDATION_INFO.cin}</span></div>
                <div>PAN: <span className="font-medium">{FOUNDATION_INFO.pan}</span></div>
                <div>80G No.: <span className="font-medium">{FOUNDATION_INFO.reg80g}</span></div>
                <div>NGO Darpan: <span className="font-medium">{FOUNDATION_INFO.darpan}</span></div>
              </div>
            </div>
          </div>

          {/* CSR Enquiry Form */}
          <div className="lg:col-span-6">
            <div className="rounded-3xl border border-[#0A2540]/10 bg-white p-8 md:p-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#D4AF37]">
                <Building2 className="w-4 h-4" /> CSR Enquiry
              </div>
              <h2 className="mt-3 font-serif-display text-2xl md:text-3xl text-[#0A2540]">
                Let's design your CSR impact together.
              </h2>

              {done ? (
                <div className="mt-8 rounded-2xl bg-[#F9F8F6] border border-[#D4AF37]/40 p-6 text-[#0A2540]">
                  <div className="font-serif-display text-xl">Thank you!</div>
                  <p className="mt-2 text-sm text-[#4A5568]">
                    Our partnerships team will get in touch within 2 working days with a
                    tailored CSR proposal.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="csr-form" className="mt-6 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Company name*</Label>
                      <Input required value={form.company_name} onChange={set("company_name")} data-testid="csr-company-name" className="mt-2 h-11" />
                    </div>
                    <div>
                      <Label>Contact person*</Label>
                      <Input required value={form.contact_person} onChange={set("contact_person")} data-testid="csr-contact-person" className="mt-2 h-11" />
                    </div>
                    <div>
                      <Label>Work email*</Label>
                      <Input required type="email" value={form.email} onChange={set("email")} data-testid="csr-email" className="mt-2 h-11" />
                    </div>
                    <div>
                      <Label>Phone*</Label>
                      <Input required value={form.phone} onChange={set("phone")} data-testid="csr-phone" className="mt-2 h-11" />
                    </div>
                    <div>
                      <Label>Designation</Label>
                      <Input value={form.designation} onChange={set("designation")} data-testid="csr-designation" className="mt-2 h-11" />
                    </div>
                    <div>
                      <Label>Indicative CSR budget</Label>
                      <Select value={form.budget_range} onValueChange={(v) => setForm((f) => ({ ...f, budget_range: v }))}>
                        <SelectTrigger className="mt-2 h-11" data-testid="csr-budget">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          {BUDGETS.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Initiatives of interest</Label>
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                      {INITIATIVES.map((i) => (
                        <label
                          key={i.slug}
                          className="flex items-start gap-3 rounded-xl border border-[#0A2540]/10 p-3 cursor-pointer hover:border-[#0A2540]/30"
                        >
                          <Checkbox
                            checked={selected.includes(i.title)}
                            onCheckedChange={() => toggleInit(i.title)}
                            data-testid={`csr-init-${i.slug}`}
                          />
                          <span className="text-sm text-[#0A2540]">{i.title}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label>Anything specific you'd like us to know?</Label>
                    <Textarea rows={3} value={form.message} onChange={set("message")} data-testid="csr-message" className="mt-2" />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="csr-submit-btn"
                    className="w-full sm:w-auto rounded-full bg-[#0A2540] text-white hover:bg-[#D4AF37] hover:text-[#0A2540] px-8 py-6 text-base font-medium"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : "Submit CSR enquiry"}
                  </Button>
                  <p className="text-xs text-[#4A5568]">
                    We treat your enquiry confidentially. Section 80G benefits available on eligible CSR contributions.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
